import {
  Box,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Link,
  Tooltip,
} from "@chakra-ui/react";
import React from "react";
import { ContextInput } from "@/app/(inbox)/components/FormElements/ContextInput";
import { SequenceInput } from "@/app/(inbox)/components/FormElements/SequenceInput";
import { TemplateInput } from "@/app/(inbox)/components/FormElements/TemplateInput";
import type { OutreachSubmitValue } from "@/app/components/RecManualField/useSubmitRecManual";
import { Feature, isFeatureEnabled } from "@/app/state/featureFlag";
import { icons } from "@/app/theme";
import { RecKind } from "@/generated/graphql";

export enum INPUT_TYPE {
  SEQUENCE = "SEQUENCE",
  TEMPLATE = "TEMPLATE",
  PROMPT = "PROMPT",
  TASK = "TASK",
}

export enum MODAL_TYPE {
  RULE = "RULE",
  OUTREACH = "OUTREACH",
}

export const OutreachInputGroup = ({
  value,
  onChange,
  inputType,
  setInputType,
  modalType,
  allowFollowUp,
  disableCadence,
}: {
  value: OutreachSubmitValue;
  onChange: (value: OutreachSubmitValue) => void;
  inputType: INPUT_TYPE;
  setInputType: (inputType: INPUT_TYPE) => void;
  modalType: MODAL_TYPE;
  allowFollowUp?: boolean;
  disableCadence?: boolean;
}) => {
  const inputTypeLabels = {
    [INPUT_TYPE.PROMPT]: (
      <>
        <Icon color="theme.virtuous.color.icon.brand.default" mr="5px" as={icons.AI} />
        AI Quick Draft
      </>
    ),
    [INPUT_TYPE.TEMPLATE]: "Template",
    [INPUT_TYPE.SEQUENCE]: "Cadence",
    [INPUT_TYPE.TASK]: "Task",
  };

  const createSwitchHandler = (
    targetInputType: INPUT_TYPE,
    resetProps: { [key: string]: null },
  ) => {
    return () => {
      setInputType(targetInputType);
      onChange({
        ...value,
        ...resetProps,
        contactMethod: targetInputType === INPUT_TYPE.TASK ? RecKind.Task : RecKind.Email,
      });
    };
  };

  const handleSwitchToPrompt = createSwitchHandler(INPUT_TYPE.PROMPT, {
    templateId: null,
    contextFromMgo: null,
    sequenceId: null,
  });

  const handleSwitchToTemplate = createSwitchHandler(INPUT_TYPE.TEMPLATE, {
    templateId: null,
    sequenceId: null,
    contextFromMgo: null,
  });

  const handleSwitchToSequence = createSwitchHandler(INPUT_TYPE.SEQUENCE, {
    templateId: null,
    sequenceId: null,
    contextFromMgo: null,
  });

  const handleSwitchToTask = createSwitchHandler(INPUT_TYPE.TASK, {
    templateId: null,
    sequenceId: null,
    contextFromMgo: null,
  });

  const renderSwitchLinks = (inputType: INPUT_TYPE) => {
    const links: { id: number; label: string; onClick: () => void; target: INPUT_TYPE }[] = [
      { id: 1, label: "Template", onClick: handleSwitchToTemplate, target: INPUT_TYPE.TEMPLATE },
      {
        id: 2,
        label: "Cadence",
        onClick: handleSwitchToSequence,
        target: INPUT_TYPE.SEQUENCE,
      },
      { id: 3, label: "AI Quick Draft", onClick: handleSwitchToPrompt, target: INPUT_TYPE.PROMPT },
      ...(isFeatureEnabled(Feature.Tasks)
        ? [{ id: 4, label: "Task", onClick: handleSwitchToTask, target: INPUT_TYPE.TASK }]
        : []),
    ];

    const filteredLinks = links.filter((link) => link.target !== inputType);

    return (
      <Flex justifyContent="space-between">
        <Flex alignItems="center" gap={2}>
          {filteredLinks.map((link, index) => {
            const isCadenceDisabled = disableCadence && link.target === INPUT_TYPE.SEQUENCE;
            const linkElement = (
              <Link
                onClick={isCadenceDisabled ? undefined : link.onClick}
                color={isCadenceDisabled ? "gray.400" : "blue.600"}
                _hover={isCadenceDisabled ? {} : { color: "blue.500" }}
                cursor={isCadenceDisabled ? "not-allowed" : "pointer"}
              >
                {link.label}
              </Link>
            );

            return (
              <React.Fragment key={link.id}>
                {isCadenceDisabled ? (
                  <Tooltip
                    label="Rec cannot be updated to a cadence. Please create a new cadence instead."
                    hasArrow
                    placement="top"
                  >
                    {linkElement}
                  </Tooltip>
                ) : (
                  linkElement
                )}
                {index < filteredLinks.length - 1 && (
                  <Divider borderColor="blue.600" orientation="vertical" h={4} />
                )}
              </React.Fragment>
            );
          })}
        </Flex>
        {modalType === MODAL_TYPE.OUTREACH &&
          inputType === INPUT_TYPE.PROMPT &&
          value.contactMethod === RecKind.Email &&
          (allowFollowUp ?? true) && (
            <Checkbox
              isChecked={value.isFollowUpNeeded}
              onChange={(e) => onChange({ ...value, isFollowUpNeeded: e.target.checked })}
            >
              Follow up if no reply?
            </Checkbox>
          )}
        {modalType === MODAL_TYPE.OUTREACH && inputType === INPUT_TYPE.TASK && (
          <Checkbox
            isChecked={value.logToCrm}
            onChange={(e) => onChange({ ...value, logToCrm: e.target.checked })}
          >
            Log to CRM on completion
          </Checkbox>
        )}
      </Flex>
    );
  };

  return (
    <FormControl>
      <Flex flexDirection="column" gap={2}>
        <Flex justifyContent="space-between" alignItems="center">
          <FormLabel fontWeight="bold" m={0}>
            {modalType === MODAL_TYPE.RULE
              ? "Key points to communicate"
              : inputTypeLabels[inputType]}
          </FormLabel>
        </Flex>

        {inputType === INPUT_TYPE.SEQUENCE && (
          <SequenceInput
            value={{
              sequenceId: value.sequenceId ?? null,
            }}
            onChange={({ sequenceId }) => {
              onChange({
                ...value,
                sequenceId,
              });
            }}
          />
        )}
        {inputType === INPUT_TYPE.TEMPLATE && (
          <TemplateInput
            value={{
              templateId: value.templateId ?? null,
              contactMethod: value.contactMethod,
            }}
            onChange={({ templateId, contactMethod }) => {
              onChange({
                ...value,
                templateId,
                contactMethod: contactMethod ?? RecKind.Email,
              });
            }}
          />
        )}
        {inputType === INPUT_TYPE.PROMPT && (
          <ContextInput
            value={{
              contextFromMgo: value.contextFromMgo,
            }}
            onChange={({ contextFromMgo }) => {
              onChange({
                ...value,
                contextFromMgo,
                templateId: null,
              });
            }}
          />
        )}
        {inputType === INPUT_TYPE.TASK && (
          <ContextInput
            value={{
              contextFromMgo: value.contextFromMgo,
            }}
            onChange={({ contextFromMgo }) => {
              onChange({
                ...value,
                contextFromMgo,
              });
            }}
            placeholder="Describe the task instructions"
          />
        )}
      </Flex>
      <Flex justifyContent="space-between" alignItems="center" mt={2}>
        <Flex alignItems="center" width="100%">
          <Box width="100%">
            {inputType === INPUT_TYPE.PROMPT && renderSwitchLinks(INPUT_TYPE.PROMPT)}
            {inputType === INPUT_TYPE.SEQUENCE && renderSwitchLinks(INPUT_TYPE.SEQUENCE)}
            {inputType === INPUT_TYPE.TEMPLATE && renderSwitchLinks(INPUT_TYPE.TEMPLATE)}
            {inputType === INPUT_TYPE.TASK && renderSwitchLinks(INPUT_TYPE.TASK)}
          </Box>
        </Flex>
      </Flex>
    </FormControl>
  );
};
